/**
 * The flight search's supplyType, keyed by that search's `encryptedKey`.
 *
 * Why a synchronous sessionStorage map and not React state: the chat card
 * click primes `flightData` and fires /flight/check (or the fares fetch) in the
 * SAME tick, so a reader of `flightData` still sees the previous search. Keying
 * by `encryptedKey` also means a value can never leak from one search into the
 * requests of another — every builder already holds the encryptedKey of the
 * search it belongs to (`id: ${encryptedKey}-${uniqueId}`).
 *
 * Same defensive pattern as linkCheckpointStore.ts: every access is wrapped,
 * a failure (private mode, quota) just means the value is not sent.
 */
import { normalizeSupplyType, SupplyType } from "../supplyType";

const KEY = "_zenvoya_flight_supply_type";
const MAX_ENTRIES = 20;

// In-memory mirror, so a failed sessionStorage write (private mode, quota)
// still serves reads in this tab instead of silently dropping the value.
let memory: Record<string, SupplyType> | null = null;

const readMap = (): Record<string, SupplyType> => {
  if (memory) return { ...memory };
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeMap = (map: Record<string, SupplyType>): void => {
  memory = { ...map };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* private mode / quota — the in-memory mirror still answers */
  }
};

/** Records (or, with no valid value, forgets) the supplyType of the search behind `encryptedKey`. */
export const setFlightSupplyType = (encryptedKey: string | undefined | null, value: unknown): void => {
  if (!encryptedKey) return;
  const map = readMap();
  delete map[encryptedKey];
  const supplyType = normalizeSupplyType(value);
  if (supplyType) map[encryptedKey] = supplyType;
  // Insertion order is preserved for string keys, so the oldest entries go first.
  const keys = Object.keys(map);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete map[k];
  writeMap(map);
};

export const getFlightSupplyType = (encryptedKey: string | undefined | null): SupplyType | undefined => {
  if (!encryptedKey) return undefined;
  return normalizeSupplyType(readMap()[encryptedKey]);
};

/**
 * The store's value for `encryptedKey`, else `fallback` (e.g. the value already
 * stamped on flightAvailabilityData, which outlives a new tab's empty
 * sessionStorage because it is persisted in localStorage).
 */
export const resolveFlightSupplyType = (
  encryptedKey: string | undefined | null,
  fallback?: unknown
): SupplyType | undefined => getFlightSupplyType(encryptedKey) ?? normalizeSupplyType(fallback);

/**
 * The supplyType to send with the PICKED flight (prebook `flightSupplyType`).
 *
 * The value stamped on flightAvailabilityData wins: every write that changes
 * the pick stamps it for that pick's search, including the retry-flow 404 that
 * swaps in a new transactionId while `encryptedKey` still names the previous
 * search — there a store lookup by `encryptedKey` would return the OLD search's
 * value next to the new transactionId. The store is the fallback for state
 * persisted before the field existed.
 */
export const resolvePickedFlightSupplyType = (
  flightAvailabilityData: { supplyType?: unknown; encryptedKey?: string | null } | null | undefined
): SupplyType | undefined =>
  normalizeSupplyType(flightAvailabilityData?.supplyType) ?? getFlightSupplyType(flightAvailabilityData?.encryptedKey);
